// ==============================
// Merge history — a log of profile merges with a safe "Undo" so a wrong merge
// can be reversed (the merged accounts are restored and their services move
// back). Rendered under the customer "Group" tab. Backed by GET /user/merge-logs
// and POST /user/merge-logs/:id/revert.
// ==============================

import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { History, Undo } from '@mui/icons-material';

import { useTranslation } from '../i18n';
import { apiGet, apiPost } from '../utils/apiClient';
import { ManagementTableHeaderCell } from './common/ManagementTable';
import { notifyActionSuccess, notifyFailure } from '../utils/toast';

type MergeActor = { id: number; name: string; email: string } | null;

type MergeLog = {
  id: number;
  primaryId: number;
  mergedIds: number[];
  status: 'merged' | 'reverted';
  createdAt: string;
  revertedAt: string | null;
  performedBy: MergeActor;
  revertedBy: MergeActor;
  details: {
    primaryLabel?: string;
    mergedLabels?: { id: number; label: string }[];
    counts?: {
      registrations?: number;
      trialBookings?: number;
      intakeResponses?: number;
      conversions?: number;
    };
  };
};

type Props = {
  /** Bump to force a refresh (e.g. right after a merge completes). */
  reloadToken?: number;
  /** Called after a successful undo so the group list can refresh. */
  onReverted?: () => void;
};

const movedCount = (log: MergeLog): number => {
  const c = log.details?.counts ?? {};
  return (
    (c.registrations ?? 0) +
    (c.trialBookings ?? 0) +
    (c.intakeResponses ?? 0) +
    (c.conversions ?? 0)
  );
};

const MergeHistoryCard = ({ reloadToken, onReverted }: Props) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<MergeLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [undoTarget, setUndoTarget] = useState<MergeLog | null>(null);
  const [reverting, setReverting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<{
        data: MergeLog[];
        metadata: { total: number };
      }>(`/user/merge-logs?page=${page + 1}&limit=${rowsPerPage}`);
      setLogs(res.data);
      setTotal(res.metadata.total);
    } catch {
      notifyFailure();
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  const handleUndo = async () => {
    if (!undoTarget) return;
    setReverting(true);
    try {
      const res = await apiPost<{
        data: { restoredAccounts: number };
      }>(`/user/merge-logs/${undoTarget.id}/revert`, {});
      notifyActionSuccess(
        t('mergeHistory.undoSuccess', {
          count: res.data.restoredAccounts,
        }),
      );
      setUndoTarget(null);
      void load();
      onReverted?.();
    } catch {
      notifyFailure();
    } finally {
      setReverting(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ p: 3, mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <History fontSize="small" color="secondary" />
        <Typography variant="h6" fontWeight={700} color="secondary.main">
          {t('mergeHistory.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('mergeHistory.description')}
      </Typography>

      {loading ? (
        <CircularProgress size={22} />
      ) : logs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('mergeHistory.empty')}
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <ManagementTableHeaderCell>
                  {t('mergeHistory.colWhen')}
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  {t('mergeHistory.colPrimary')}
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  {t('mergeHistory.colMerged')}
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  {t('mergeHistory.colMoved')}
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  {t('mergeHistory.colBy')}
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell align="right">
                  {t('mergeHistory.colActions')}
                </ManagementTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(log.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {log.details?.primaryLabel || `#${log.primaryId}`}
                  </TableCell>
                  <TableCell>
                    {(log.details?.mergedLabels ?? [])
                      .map((m) => m.label)
                      .join(', ') || log.mergedIds.join(', ')}
                  </TableCell>
                  <TableCell>
                    {t('mergeHistory.itemsMoved', { count: movedCount(log) })}
                  </TableCell>
                  <TableCell>{log.performedBy?.name || '—'}</TableCell>
                  <TableCell align="right">
                    {log.status === 'merged' ? (
                      <Button
                        size="small"
                        color="warning"
                        startIcon={<Undo />}
                        onClick={() => setUndoTarget(log)}
                      >
                        {t('mergeHistory.undo')}
                      </Button>
                    ) : (
                      <Chip
                        size="small"
                        label={t('mergeHistory.status_reverted')}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}

      <Dialog open={undoTarget !== null} onClose={() => setUndoTarget(null)}>
        <DialogTitle>{t('mergeHistory.undoConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('mergeHistory.undoConfirmBody', {
              primary:
                undoTarget?.details?.primaryLabel ||
                `#${undoTarget?.primaryId ?? ''}`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUndoTarget(null)} disabled={reverting}>
            {t('mergeHistory.cancel')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            startIcon={
              reverting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Undo />
              )
            }
            disabled={reverting}
            onClick={() => void handleUndo()}
          >
            {t('mergeHistory.undo')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MergeHistoryCard;
